export default angular.module('eventix.common.services.crudctrl',[])
    .factory('CrudCtrl', CrudCtrlFactory).name;

function CrudCtrlFactory($log, UIMessages, $state, $transitions, ConfirmDelete, $q, $rootScope) {
    const hooks = [
        'beforeList',
        'beforeLoad', 'afterLoad',
        'beforeNew', 'beforeEdit',
        'beforeSave', 'afterSave',
        'beforeDelete', 'afterDelete',
        'beforeAttach', 'afterAttach',
        'beforeDetach', 'afterDetach',
        'beforeQuery', 'afterQuery',
        'beforeReorder', 'afterReorder',
        'beforeEditDraft',
        'beforeSaveDraft', 'afterSaveDraft',
        'beforeDeleteDraft', 'afterDeleteDraft'
    ];

    /**
     * @ngdoc service
     * @name eventix.common.services.CrudCtrl
     * @constructor
     * @description
     * This module creates a Controller-like object with methods and states common to managing a CRUD model.
     * It's the successor to SimpleCRUD, which had aged less than gracefully.
     *
     * Attention: this is a constructor function, **don't forget the `new` keyword**
     *
     * @param {Array<BaseClass>|Promise<Array>} models The list of model objects or the promise thereof
     * @param {Function} newModelFn A function that returns a new instance of the model to manage
     * @param {Object} [options={}] Additional options.
     * ```
     * // Allow CrudCtrl to determine state on load according to context. False will default to `list`.
     * // This option is useful when you've got multiple CrudCtrls on a single page
     * options.determineInitState: true
     * options.autoNew       // Auto-transition to `new` on init if models.length < 1
     * options.stateParamNew // Auto-transition to `new` on init if $stateParams.new != false
     * options.persistState  // Persist internal state to state parameters
     * ```
     */
    function CrudCtrl(models, newModelFn, options = {}) {
        const $ctrl = this;
        $ctrl.hooks = {};
        $ctrl.relationships = {};
        $ctrl.newModelFn = newModelFn;
        $ctrl.model = newModelFn();
        $ctrl.state = 'loading';
        $ctrl.options = _.defaults({}, options, {
            determineInitState: true,
            autoNew: true,
            stateParamNew: true,
            persistState: true
        });
        let promise;
        if(!(models instanceof Array) && typeof models.then === 'function')
            promise = $ctrl._loadModelsFromPromise(models);
        else if(typeof models === 'function')
            promise = $ctrl._loadModelsFromPromise(models());
        else if(models instanceof Array)
            promise = $q.resolve($ctrl.models = models);
        else {
            $ctrl.handleError('CrudCtrl: Invalid set of models given (not a promise or array)');
            promise = $q.resolve($ctrl.models = []);
        }

        promise.then(() => {
            $ctrl._respondToStateParams($state.params);
        });

        let scopeDeregister = $rootScope.$on('input-blur', (event, name) => {
            if($ctrl.state !== 'edit' && $ctrl.state !== 'new')
                return;
            if(typeof $ctrl.model.validate === 'function')
                $ctrl.model.validate(name);
            $rootScope.$applyAsync();
        });
        let paramsDeregister = $transitions.onFinish({ to: $state.current.name }, trans => {
            if(!$ctrl.options.persistState) return;
            let params = trans.params();
            if(params.new && $ctrl.state !== 'new')
                $ctrl.new();
            if(params.editing && $ctrl.state !== 'edit') {
                let pk = _.get(_.first($ctrl.models), 'constructor.primaryKey');
                if(!pk)
                    return;
                let model = _.find($ctrl.models, _.set({}, pk, params.editing));
                if(model)
                    $ctrl.edit(model);
            }
        });
        let exitDeregister = $transitions.onExit({ exiting: $state.current.name }, trans => {
            scopeDeregister();
            paramsDeregister();
            exitDeregister();
        });
    }

    var proto = CrudCtrl.prototype;

    /**
     * @ngdoc property
     * @propertyOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#models
     * @type Array<Object>
     * @description Array of models to be managed
     */
    /**
     * @ngdoc property
     * @propertyOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#model
     * @type Object
     * @description The current model being edited
     */
    /**
     * @ngdoc property
     * @propertyOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#state
     * @type String
     * @description Current state of the CrudCtrl `(loading|list|edit|new)`
     */
    /**
     * @ngdoc property
     * @propertyOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#busy
     * @type Boolean
     * @description Whether the CrudCtrl is busy doing stuff (saving, loading, deleting, running hooks)
     */
    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#beforeList
     * @description Add callback to be run before entering the `list` state. Optionally returns a promise which will block progress. Multiple callbacks are run in parallel.
     * @param {Function} fn Callback function with signature `fn(prevState) => Void|Promise`
     * @return {Function} Deregistration function
     */
    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#beforeLoad
     * @description Add callback to be run before loading async models. Optionally returns a promise which will block progress. Multiple callbacks are run in parallel.
     * @param {Function} fn Callback function with signature `fn() => Void|Promise`
     * @return {Function} Deregistration function
     */
    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#afterLoad
     * @description Add callback to be run after loading async models (successfully or not). Optionally returns a promise which will block progress. Multiple callbacks are run in parallel.
     * @param {Function} fn Callback function with the signature `fn(error, models) => Void|Promise`
     * @return {Function} Deregistration function
     */
    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#beforeNew
     * @description Add callback to be run before transitioning to `new` state. Optionally returns a promise which will block progress. Multiple callbacks are run in parallel.
     * @param {Function} fn Callback function with the signature `fn(model) => Void|Promise`
     * @return {Function} Deregistration function
     */
    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#beforeEdit
     * @description Add callback to be run before transitioning to `edit` state. Optionally returns a promise which will block progress. Multiple callbacks are run in parallel.
     * @param {Function} fn Callback function with the signature `fn(model) => Void|Promise`
     * @return {Function} Deregistration function
     */
    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#beforeSave
     * @description Add callback to be run before saving a model. Optionally returns a promise which will block progress. Multiple callbacks are run in parallel.
     * @param {Function} fn Callback function with the signature `fn(model) => Void|Promise`
     * @return {Function} Deregistration function
     */
    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#afterSave
     * @description Add callback to be run after a model is saved (successfully or not). Optionally returns a promise which will block progress. Multiple callbacks are run in parallel.
     * @param {Function} fn Callback function with the signature `fn(error, model, isNew) => Void|Promise`
     * @return {Function} Deregistration function
     */
    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#beforeDelete
     * @description Add callback to be run before a user is asked to confirm deleting a model. Optionally returns a promise which will block progress. Multiple callbacks are run in parallel.
     * @param {Function} fn Callback function with the signature `fn(model) => Void|Promise`
     * @return {Function} Deregistration function
     */
    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#afterDelete
     * @description Add callback to be run after a model is deleted (successfully or not). Optionally returns a promise which will block progress. Multiple callbacks are run in parallel.
     * @param {Function} fn Callback function with the signature `fn(error, model) => Void|Promise`
     * @return {Function} Deregistration function
     */
    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#beforeReorder
     * @description Add callback to be run before reordering children. Optionally returns a promise which will block progress. Multiple callbacks are run in parallel.
     * @param {Function} fn Callback function with the signature `fn(fnSuffix, model, predecessor, successor) => Void|Promise`
     * @return {Function} Deregistration function
     */
    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#afterReorder
     * @description Add callback to be run after reordering children (successfully or not). Optionally returns a promise which will block progress. Multiple callbacks are run in parallel.
     * @param {Function} fn Callback function with the signature `fn(error, fnSuffix, model, predecessor, successor) => Void|Promise`
     * @return {Function} Deregistration function
     */
    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#beforeQuery
     * @description Add callback to be run before querying a belongsToMany. Optionally returns a promise which will block progress. Multiple callbacks are run in parallel.
     * @param {Function} fn Callback function with the signature `fn(fnSuffix, model) => Void|Promise`
     * @return {Function} Deregistration function
     */
    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#afterQuery
     * @description Add callback to be run after querying a belongsToMany (successfully or not). Optionally returns a promise which will block progress. Multiple callbacks are run in parallel.
     * @param {Function} fn Callback function with the signature `fn(error, fnSuffix, model, children) => Void|Promise`
     * @return {Function} Deregistration function
     */
    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#beforeAttach
     * @description Add callback to be run before attaching a belongsToMany (successfully or not). Optionally returns a promise which will block progress. Multiple callbacks are run in parallel.
     * @param {Function} fn Callback function with the signature `fn(fnSuffix, model, child, isCreating) => Void|Promise`
     * @return {Function} Deregistration function
     */
    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#afterAttach
     * @description Add callback to be run after attaching a belongsToMany (successfully or not). Optionally returns a promise which will block progress. Multiple callbacks are run in parallel.
     * @param {Function} fn Callback function with the signature `fn(error, fnSuffix, model, child, isCreating) => Void|Promise`
     * @return {Function} Deregistration function
     */
    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#beforeDetach
     * @description Add callback to be run before detaching a belongsToMany (successfully or not). Optionally returns a promise which will block progress. Multiple callbacks are run in parallel.
     * @param {Function} fn Callback function with the signature `fn(fnSuffix, model, child) => Void|Promise`
     * @return {Function} Deregistration function
     */
    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#afterDetach
     * @description Add callback to be run after detaching a belongsToMany (successfully or not). Optionally returns a promise which will block progress. Multiple callbacks are run in parallel.
     * @param {Function} fn Callback function with the signature `fn(error, fnSuffix, model, child) => Void|Promise`
     * @return {Function} Deregistration function
     */
    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#beforeEditDraft
     * @description Add callback to be run before transitioning to `new` state. Optionally returns a promise which will block progress. Multiple callbacks are run in parallel.
     * @param {Function} fn Callback function with the signature `fn(model) => Void|Promise`
     * @return {Function} Deregistration function
     */
    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#beforeSaveDraft
     * @description Add callback to be run before saving a model as draft. Optionally returns a promise which will block progress. Multiple callbacks are run in parallel.
     * @param {Function} fn Callback function with the signature `fn(model) => Void|Promise`
     * @return {Function} Deregistration function
     */
    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#afterSaveDraft
     * @description Add callback to be run after a model is saved as draft (successfully or not). Optionally returns a promise which will block progress. Multiple callbacks are run in parallel.
     * @param {Function} fn Callback function with the signature `fn(error, model, isNew) => Void|Promise`
     * @return {Function} Deregistration function
     */
    hooks.forEach(hook => {
        proto[hook] = function(fn) {
            const $ctrl = this;
            $ctrl.hooks[hook] = $ctrl.hooks[hook] || [];
            $ctrl.hooks[hook].push(fn);
            return function() {
                _.pull($ctrl.hooks[hook], fn);
            };
        };
    });

    /**
     * Derive internal state of CrudCtrl from a set of StateParams
     * @param {Object} stateParams Object with current state parameters
     * @return {Promise} Resolves when the destination state is reached
     */
    proto._respondToStateParams = function(stateParams) {
        const $ctrl = this;
        let promise;
        if(!$ctrl.options.determineInitState)
            promise = $ctrl.list();
        else if(stateParams.editing && $ctrl.models.length) {
            let pk = $ctrl.models[0].constructor.primaryKey;
            let filter = _.set({}, pk, stateParams.editing);
            let model = _.find($ctrl.models, filter);
            if(model)
                promise = $ctrl.edit(model);
            else if(!$ctrl.models.length && $ctrl.options.autoNew)
                promise = $ctrl.new();
            else
                promise = $ctrl.list();
        } else if(stateParams.new !== undefined && $ctrl.options.stateParamNew)
            promise = $ctrl.new();
        else {
            promise = $ctrl.models.length === 0 && $ctrl.options.autoNew ?
                $ctrl.new() : $ctrl.list();
        }

        return promise.catch(() => {
            $ctrl.list();
        });
    };

    /**
     * Load models from asynchronous source
     * @param {Promise<Array>} promise The promise of models
     * @return {Promise} Resolves when the loaded. If the promise rejects, error is handled
     * and CrudCtrl is initiated with an empty array
     */
    proto._loadModelsFromPromise = function(promise) {
        const $ctrl = this;
        return $q.resolve()
            .then(() => $ctrl.fire('beforeLoad'))
            .then(() => $q.resolve(promise))
            .then(models => {
                $ctrl.models = models;
                return $ctrl.fire('afterLoad', null, models);
            }, error => {
                $ctrl.models = [];
                return $ctrl.fire('afterLoad', error, $ctrl.models);
            });
    };

    /**
     * Handle an error by logging it to console. If it has a description, show that to the user
     * @param {String|Object} error An error string or object
     * @return {Promise<error>} A rejecting promise containing the original error
     */
    proto.handleError = function(error) {
        $log.error(error);
        if(error.description && error.description.length)
            UIMessages.push({ message: error.description, type: 'error' });
        return $q.reject(error);
    };

    /**
     * Fire a hook.
     *
     * Callbacks on the hook are fired sequentially, waiting for the previous to finish
     * @param {String} hook Name of the hook to fire
     * @param {Array<any>} args Additional arguments are passed to the hooks
     * @return {Promise<void>} Returns a promise that resolves when all hooks resolved or rejects when one hook does.
     */
    proto.fire = function(hook, ...args) {
        const $ctrl = this;
        return _.reduce(
            $ctrl.hooks[hook],
            (promise, fn) => promise.then(() => $q.resolve(fn(...args))),
            $q.resolve()
        );
    };

    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#delete
     * @description Delete a model. Calls beforeHook, opens a ConfirmDelete modal, optionally deletes the model and runs the afterDelete hook
     * @param {Object} model A model from the `CrudCtrl.models` list
     * @return {Promise<void>} An empty promise that resolves when it settles in the `list` state
     */
    proto.delete = function(model) {
        const $ctrl = this;
        const isDraft = model[model.constructor.primaryKey] === undefined;
        if(isDraft)
            return $ctrl.deleteDraft(model);
        $ctrl.busy = true;
        return $ctrl.fire('beforeDelete', model)
            .then(() => ConfirmDelete.open(model).catch(e => $q.reject('cancelled')))
            .then(() => model.$delete())
            .then(() => _.pull($ctrl.models, model))
            .then(
                () => $ctrl.fire('afterDelete', null, model),
                error => $ctrl.fire('afterDelete', error, model)
                    .then(() => $q.reject(error))
            )
            .then(
                () => $ctrl.list(),
                error => {
                    $ctrl.busy = false;
                    return $ctrl.handleError(error);
                }
            );
    };

    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#deleteDraft
     * @description Delete a draft model. Calls beforeDeleteDraft hook, opens a ConfirmDelete modal, optionally deletes the model and runs the afterDeleteDraft hook
     * @param {Object} model A model from the `CrudCtrl.models` list
     * @return {Promise<void>} An empty promise that resolves when it settles in the `list` state
     */
    proto.deleteDraft = function(model) {
        const $ctrl = this;
        const isDraft = model[model.constructor.primaryKey] === undefined;
        if(!isDraft)
            return $q.reject('Not a draft');
        $ctrl.busy = true;
        return $ctrl.fire('beforeDeleteDraft', model)
            .then(() => ConfirmDelete.open(model).catch(e => $q.reject('cancelled')))
            .then(() => _.pull($ctrl.models, model))
            .then(
                () => $ctrl.fire('afterDeleteDraft', null, model),
                error => $ctrl.fire('afterDeleteDraft', error, model)
                    .then(() => $q.reject(error))
            )
            .then(
                () => $ctrl.list(),
                error => {
                    $ctrl.busy = false;
                    return $ctrl.handleError(error);
                }
            );
    };

    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#list
     * @description Transition to `list` state
     * @return {Promise<void>} An empty promise that resolves when it settles in the `list` state
     */
    proto.list = function() {
        const $ctrl = this;
        $ctrl.busy = true;
        return $ctrl.fire('beforeList', $ctrl.state)
            .then(() => {
                if($ctrl.state === 'edit') {
                    if($ctrl.model.$savedState)
                        restoreToSavedState($ctrl.model, $ctrl.model.$savedState);
                    if($ctrl.model.$errors && $ctrl.model.$errors.clear)
                        $ctrl.model.$errors.clear();
                }
                $ctrl.busy = false;
                $ctrl.state = 'list';
                let stateParams = _.cloneDeep($state.params);
                delete stateParams.new;
                delete stateParams.editing;
                if($ctrl.options.persistState)
                    return $state.go($state.current.name, stateParams, { inherit: false });
                return $q.resolve();
            }).catch(error => {
                $ctrl.busy = false;
                return $ctrl.handleError(error);
            });
    };

    function restoreToSavedState(dst, src) {
        for(let attr in src) {
            if(!dst.hasOwnProperty(attr))
                continue;
            if(!angular.isObject(src[attr]) || angular.isArray(src[attr]))
                dst[attr] = src[attr];
            else
                dst[attr] = restoreToSavedState(dst[attr] || {}, src[attr]);
        }
        return dst;
    }

    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#edit
     * @description Transition to `edit` state
     * @param {Model} model A model to edit
     * @return {Promise<void>} An empty promise that resolves when it settles in the `edit` state
     */
    proto.edit = function(model) {
        const $ctrl = this;
        $ctrl.busy = true;
        const isDraft = model[model.constructor.primaryKey] === undefined;
        if(isDraft)
            return $ctrl.editDraft(model);
        return $ctrl.fire('beforeEdit', model)
            .then(() => {
                $ctrl.busy = false;
                $ctrl.model = model;
                $ctrl.state = 'edit';

                let stateParams = _.cloneDeep($state.params);
                let pk = model.constructor.primaryKey;
                _.set(stateParams, 'editing', model[pk]);
                delete stateParams.new;
                if($ctrl.options.persistState)
                    return $state.go($state.current.name, stateParams);
                return $q.resolve();
            }, error => {
                $ctrl.busy = false;
                return $ctrl.handleError(error);
            });
    };

    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#editDraft
     * @description Transition to `new` state with a model that was previously saved as draft
     * @param {Model} model A drafted model to edit
     * @return {Promise<void>} An empty promise that resolves when it settles in the `new` state
     */
    proto.editDraft = function(model) {
        const $ctrl = this;
        $ctrl.busy = true;
        const isDraft = model[model.constructor.primaryKey] === undefined;
        if(!isDraft)
            return $q.reject('Model is not a draft');
        return $ctrl.fire('beforeEditDraft', model)
            .then(() => {
                $ctrl.busy = false;
                $ctrl.model = model;
                $ctrl.state = 'new';

                let stateParams = _.cloneDeep($state.params);
                stateParams.new = true;
                delete stateParams.editing;
                if($ctrl.options.persistState)
                    return $state.go($state.current.name, stateParams);
                return $q.resolve();
            }, error => {
                $ctrl.busy = false;
                return $ctrl.handleError(error);
            });
    };

    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#new
     * @description Transition to `new` state
     * @return {Promise<void>} An empty promise that resolves when it settles in the `list` state
     */
    proto.new = function() {
        const $ctrl = this;
        $ctrl.busy = true;
        return $q.resolve($ctrl.newModelFn())
            .then(model => {
                return $ctrl.fire('beforeNew', model)
                    .then(() => model);
            })
            .then(model => {
                $ctrl.busy = false;
                $ctrl.model = model;
                if($ctrl.model.$errors && $ctrl.model.$errors.clear)
                    $ctrl.model.$errors.clear();
                $ctrl.state = 'new';

                let stateParams = _.cloneDeep($state.params);
                delete stateParams.editing;
                stateParams.new = true;
                if($ctrl.options.persistState)
                    return $state.go($state.current.name, stateParams);
                return $q.resolve();
            }, error => {
                $ctrl.busy = false;
                return $ctrl.handleError(error);
            });
    };

    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#save
     * @description Save a model. Calls before hook, saves the model and runs the after hook. Won't do anything if `model.$invalid`
     * @return {Promise<void>} An promise that resolves or rejects with the error
     */
    proto.save = function() {
        const $ctrl = this;
        const model = $ctrl.model;
        if(model.$invalid)
            return $q.reject('Model invalid');
        $ctrl.busy = true;
        const isNew = model[model.constructor.primaryKey] === undefined;
        return $ctrl.fire('beforeSave', model)
            .then(() => model.$save())
            .then(
                () => {
                    if(isNew)
                        $ctrl.models.push(model);
                    return $ctrl.fire('afterSave', null, model, isNew);
                },
                error => $ctrl.fire('afterSave', error, model, isNew).then(() => $q.reject(error))
            )
            .then(
                () => $ctrl.list(),
                error => {
                    $ctrl.busy = false;
                    return $ctrl.handleError(error);
                }
            );
    };

    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#saveDraft
     * @description
     * New models can optionally be saved as draft. It won't call the `model.$save()` method but *will* add the model
     * to the `models` array. The hooks beforeSaveDraft and afterSaveDraft will be called.  A previously saved model
     * (with an existing primary key) can't be saved as draft. Won't do anything if `model.$invalid`.
     * @return {Promise<void>} An promise that resolves or rejects with the error
     */
    proto.saveDraft = function() {
        const $ctrl = this;
        const model = $ctrl.model;
        if(model.$invalid)
            return $q.reject('Model invalid');
        $ctrl.busy = true;
        const isNew = model[model.constructor.primaryKey] === undefined;
        if(!isNew)
            return $q.reject('Cant save a pre-existing model as draft');
        return $ctrl.fire('beforeSaveDraft', model)
            .then(
                () => {
                    if(!_.includes($ctrl.models, model))
                        $ctrl.models.push(model);
                    return $ctrl.fire('afterSaveDraft', null, model, isNew);
                },
                error => $ctrl.fire('afterSaveDraft', error, model, isNew).then(() => $q.reject(error))
            )
            .then(
                () => $ctrl.list(),
                error => {
                    $ctrl.busy = false;
                    return $ctrl.handleError(error);
                }
            );
    };

    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#addBelongsToMany
     * @description
     * Extend the CrudCtrl to manage a belongsToMany relationship
     *
     * Adds a `attach[fnSuffix]` and `detach[fnSuffix]` to the
     * CrudCtrl object. Attaches a `beforeEdit` hook to fill the
     * container with children. It also attaches a `afterSave` method
     * to attach children when creating a model.
     *
     * These functions accept one or an array models to be attached
     * or detached. They'll be run in series, each model firing the
     * before and after hooks.
     *
     * @param {String} childContainer The property name on models that contains child models
     * @param {String} fnSuffix The function suffix of relationship actions; i.e. `model.$attachSUFFIX`
     */
    proto.addBelongsToMany = function(childContainer, fnSuffix) {
        const $ctrl = this;
        // parent primaryKey
        const ppk = $ctrl.model.constructor.primaryKey;
        $ctrl.relationships[fnSuffix] = childContainer;
        $ctrl['attach' + fnSuffix] = toggleModels.bind($ctrl, 'attach');
        $ctrl['detach' + fnSuffix] = toggleModels.bind($ctrl, 'detach');
        $ctrl['reorder' + fnSuffix] = reorderModels.bind($ctrl);

        $ctrl.beforeEdit(model => {
            return $ctrl.fire('beforeQuery', fnSuffix, model)
                .then(() => model['$query' + fnSuffix]())
                .then(
                    children => {
                        Object.defineProperty(model, childContainer, {
                            value: children,
                            writable: true,
                            configurable: true
                        });
                        return $ctrl.fire('afterQuery', null, fnSuffix, model, children);
                    },
                    error => $ctrl.fire('afterQuery', error, fnSuffix, model, null).then(() => $q.reject(error))
                );
        });
        $ctrl.beforeNew(model => {
            Object.defineProperty(model, childContainer, {
                value: [],
                configurable: true,
                writable: true
            });
        });
        $ctrl.afterSave((error, model, isNew) => {
            if(error)
                return $q.resolve();
            if(isNew) {
                let children = $ctrl.model[childContainer];
                $ctrl.model[childContainer] = [];
                return $ctrl['attach' + fnSuffix](children);
            }
            return $q.resolve();
        });

        function reorderModels(pre, suc) {
            if($ctrl.busy) return $q.reject('CrudCtrl is busy');
            $ctrl.busy = true;
            if(!$ctrl.model[ppk]) {
                return $ctrl.fire('beforeReorder', fnSuffix, $ctrl.model, pre, suc).then(() => {
                    return $ctrl.fire('afterReorder', null, fnSuffix, $ctrl.model, pre, suc);
                }).finally(() => $ctrl.busy = false);
            }

            return $ctrl.fire('beforeReorder', fnSuffix, $ctrl.model, pre, suc)
                .then(() => $ctrl.model['$reorder' + fnSuffix](pre, suc))
                .then(() => $ctrl.fire('afterReorder', null, fnSuffix, $ctrl.model, pre, suc),
                    err => {
                        return $ctrl.handleError(err).catch(() => {
                            return $ctrl.fire('afterReorder', err, fnSuffix, $ctrl.model, pre, suc);
                        });
                    })
                .finally(() => $ctrl.busy = false);
        }

        // This method will be added to the CrudCtrl as attach and detach
        function toggleModels(method, children) {
            var Method = method.charAt(0).toUpperCase() + method.slice(1);
            if($ctrl.busy && $ctrl.state !== 'new')
                return $q.reject(new Error('HTTP actions in progress'));
            if(!_.isArray(children) && !_.isNil(children))
                children = [children];
            if(!$ctrl.model[childContainer])
                $ctrl.model[childContainer] = [];
            $ctrl.busy = true;

            let isCreating = $ctrl.state === 'new';
            let promiseChain = _.reduce(children, (promised, child) => {
                return promised.then(() => {
                    // Toggle each child seperately, one after the other. Calling hooks for each one.
                    return $ctrl.fire('before' + Method, fnSuffix, child, isCreating)
                        .then(() => toggleModel(method, child))
                        .then(
                            () => $ctrl.fire('after' + Method, null, fnSuffix, $ctrl.model, child, isCreating),
                            error => $ctrl.fire('after' + Method, error, fnSuffix, $ctrl.model, child, isCreating)
                                .then(() => $q.reject(error))
                        );
                });
            }, $q.resolve());

            return promiseChain
                .then(() => {
                    $ctrl.busy = false;
                    return children;
                }, error => {
                    $ctrl.busy = false;
                    return $ctrl.handleError(error);
                });
        }

        // Handles attaching or detaching of a single model
        function toggleModel(method, child) {
            if(!$ctrl.model[ppk])
                return $q.resolve( resolve(method, child) );
            var pk = child.constructor.primaryKey;
            var find = _.set({}, pk, child[pk]);
            if(method === 'attach' && _.findIndex($ctrl.model[childContainer], find) >= 0)
                return $q.resolve(); // Child is already attached
            if(method === 'detach' && _.findIndex($ctrl.model[childContainer], find) < 0)
                return $q.resolve(); // Child is not attached
            return $ctrl.model['$' + method + fnSuffix](child)
                .then( resolve.bind($ctrl, method, child));
        }

        // Handles the default after(attach|detach) action
        function resolve(method, child) {
            var pk = child.constructor.primaryKey;
            var find = _.set({}, pk, child[pk]);
            var index = _.findIndex($ctrl.model[childContainer], find);
            if(method === 'attach' && index < 0)
                $ctrl.model[childContainer].push(child);
            else if(method === 'detach' && index >= 0)
                $ctrl.model[childContainer].splice(index, 1);
            return $q.resolve(child);
        }
    };

    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#showList
     * @description
     * Helper method that checks if state is `list`
     * @returns {Boolean} True if state is `list`
     */
    proto.showList = function() {
        return this.state === 'list';
    };
    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#showForm
     * @description
     * Helper method that checks if state is `edit|new`
     * @returns {Boolean} True if state is `edit|new`
     */
    proto.showForm = function() {
        return this.state === 'edit' || this.state === 'new';
    };
    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#showEdit
     * @description
     * Helper method that checks if state is `edit`
     * @returns {Boolean} True if state is `edit`
     */
    proto.showEdit = function() {
        return this.state === 'edit';
    };
    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#showLoading
     * @description
     * Helper method that checks if state is `loading`
     * @returns {Boolean} True if state is `loading`
     */
    proto.showLoading = function() {
        return this.state === 'loading';
    };
    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl#showNew
     * @description
     * Helper method that checks if state is `new`
     * @returns {Boolean} True if state is `new`
     */
    proto.showNew = function() {
        return this.state === 'new';
    };


    /**
     * @ngdoc method
     * @methodOf eventix.common.services.CrudCtrl
     * @name eventix.common.services.CrudCtrl.new
     * @description
     * A proxy method for the constructor.
     *
     * @example
     * ```CrudCtrl.new(models, Model.new)```
     *
     * @returns {CrudCtrl} An instance of CrudCtrl
     */
    CrudCtrl.new = (...args) => new CrudCtrl(...args);

    return CrudCtrl;
}
